<template>
  <div class="step-content">
    <div class="compose-header">
      <h3>Compose Message</h3>
    </div>

    <!-- Message Composer -->
    <div class="message-composer">
      <!-- Message Saved Indicator -->
      <div v-if="savedMessageId" class="alert alert-success message-saved-alert">
        <i class="fa fa-check-circle"></i>
        <strong>Message Saved!</strong> Message ID: {{ savedMessageId }}
        <small class="d-block mt-1">Fields are now read-only. To make changes, cancel and start over.</small>
      </div>

      <div class="form-group">
        <label for="eventSelect"
          >Event <span class="required">*</span></label
        >
        <div class="event-field-container">
          <select
            id="eventSelect"
            :value="selectedEvent"
            @change="$emit('update:selectedEvent', $event.target.value)"
            class="form-control"
            :disabled="!!savedMessageId || fetchingEvents"
          >
            <option value="">Select an event...</option>
            <option
              v-for="event in events"
              :key="event.id"
              :value="event.id"
            >
              {{ event.name }}{{ event.clientEventCode ? ` (${event.clientEventCode})` : '' }}
            </option>
          </select>
          <button
            type="button"
            class="btn btn-sm btn-primary new-event-btn"
            @click="openNewEventPage"
            :disabled="!!savedMessageId"
            title="Create new event"
          >
            <i class="fa fa-plus"></i>
            New Event
          </button>
        </div>
        <div class="field-hint" v-if="events.length === 0 && !fetchingEvents">
          No active events available
        </div>
        <div class="field-hint" v-if="fetchingEvents">
          <i class="fa fa-spinner fa-spin"></i> Loading events...
        </div>
        <button
          type="button"
          class="btn btn-sm btn-link refresh-btn"
          @click="$emit('refresh-events')"
          :disabled="fetchingEvents"
          title="Refresh events list"
        >
          <i class="fa" :class="fetchingEvents ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
          Refresh Events
        </button>
      </div>

      <div class="form-group">
        <label>Template</label>
        <button
          type="button"
          class="btn btn-sm btn-info select-template-btn-full"
          @click="openTemplateModal"
          :disabled="!!savedMessageId || !selectedEvent"
          title="Select from templates"
        >
          <i class="fa fa-file-text"></i>
          Select Template
        </button>
        <div class="field-hint" v-if="!selectedEvent" style="text-align: left; color: #856404;">
          <i class="fa fa-info-circle"></i>
          Select an event first to use templates
        </div>
      </div>

      <div class="form-group">
        <label for="messageType">Priority Level</label>
        <select
          id="messageType"
          :value="messageType"
          @change="$emit('update:messageType', $event.target.value)"
          class="form-control"
          :disabled="!!savedMessageId"
        >
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>

      <div class="form-group">
        <label for="messageTypeSelect">Message Type <span class="required">*</span></label>
        <select
          id="messageTypeSelect"
          :value="selectedMessageType"
          @change="$emit('update:selectedMessageType', $event.target.value)"
          class="form-control"
          :disabled="!!savedMessageId"
        >
          <option
            v-for="msgType in messageTypes"
            :key="msgType.id"
            :value="msgType.code"
            :disabled="!msgType.isActive"
          >
            {{ msgType.name }}{{ msgType.isActive ? '' : ' (Inactive)' }}
          </option>
        </select>
        <div class="field-hint" v-if="messageTypes.length === 0 && !fetchingMessageTypes">
          No message types available
        </div>
        <div class="field-hint" v-if="fetchingMessageTypes">
          <i class="fa fa-spinner fa-spin"></i> Loading message types...
        </div>
        <div class="field-hint" v-if="selectedMessageType && getSelectedMessageTypeDescription()" style="text-align: left; color: #856404;">
          <i class="fa fa-info-circle"></i>
          {{ getSelectedMessageTypeDescription() }}
        </div>
        <div class="field-hint" v-else-if="!selectedMessageType || selectedMessageType === ''" style="text-align: left; color: #0056b3;">
          <i class="fa fa-info-circle"></i>
          Automatic mode will send via multiple channels based on priority level
        </div>
      </div>

      <div class="form-group">
        <!-- Scheduling Rules Display -->
        <div v-if="currentSchedulingRule" class="scheduling-info">
          <div class="scheduling-header">
            <div class="header-left">
              <i class="fa fa-paper-plane-o"></i>
              <strong>Delivery Flow</strong>
            </div>
            <div class="header-right" v-if="currentSchedulingRule.maxCycles > 1">
              <span class="info-badge">
                <i class="fa fa-repeat"></i>
                {{ currentSchedulingRule.maxCycles }}× cycles
              </span>
            </div>
            <div class="header-right" v-else>
              <span class="info-badge">
                <i class="fa fa-send"></i>
                Single delivery
              </span>
            </div>
          </div>
          
          <div class="scheduling-timeline">
            <div
              v-for="(item, index) in schedulingFlowItems"
              :key="item.key"
              :class="item.class"
            >
              <!-- Message Channel Step -->
              <template v-if="item.type === 'step'">
                <div class="timeline-node">
                  <div class="node-number">{{ item.number }}</div>
                  <div class="node-icon" :style="{ background: item.color }">
                    <i :class="['fa', item.icon]"></i>
                  </div>
                </div>
                <div class="timeline-label">{{ item.label }}</div>
              </template>
              
              <!-- Wait Period -->
              <template v-else-if="item.type === 'wait'">
                <div class="timeline-connector">
                  <div class="connector-line"></div>
                  <div class="connector-time">
                    <i class="fa fa-clock-o"></i>
                    {{ item.label }}
                  </div>
                </div>
              </template>
              
              <!-- Restart Loop -->
              <template v-else-if="item.type === 'restart'">
                <div class="timeline-loop">
                  <i class="fa fa-rotate-left"></i>
                  <span>Loop</span>
                </div>
              </template>
            </div>
          </div>
          
          <div class="scheduling-summary" v-if="currentSchedulingRule.maxCycles > 1">
            <div class="summary-item">
              <i class="fa fa-hourglass-half"></i>
              <span>Cycle Duration: <strong>{{ (currentSchedulingRule.messageTypeCodes.length - 1) * currentSchedulingRule.intervalMinutes + currentSchedulingRule.recycleTimeMinutes }}min</strong></span>
            </div>
            <div class="summary-item">
              <i class="fa fa-bolt"></i>
              <span>Retry Interval: <strong>{{ currentSchedulingRule.intervalMinutes }}min</strong></span>
            </div>
          </div>
          
          <div class="scheduling-summary" v-else>
            <div class="summary-item">
              <i class="fa fa-paper-plane"></i>
              <span>Message will be sent once via <strong>{{ currentSchedulingRule.messageTypeCodes[0].toUpperCase() }}</strong></span>
            </div>
          </div>
        </div>

        <div v-else-if="fetchingSchedulingRules" class="field-hint">
          <i class="fa fa-spinner fa-spin"></i> Loading scheduling rules...
        </div>
      </div>

      <div class="form-group">
        <label for="responseType">Response Option</label>
        <select
          id="responseType"
          :value="responseType"
          @change="$emit('update:responseType', $event.target.value)"
          class="form-control"
          :disabled="!!savedMessageId"
        >
          <option value="none">None</option>
          <option value="ok_not_ok">OK/Not OK</option>
        </select>
        <div class="field-hint" v-if="responseType === 'ok_not_ok'">
          Recipients will be able to respond with OK or Not OK
        </div>
      </div>

      <!-- Multi-Language Message Composer -->
      <div class="message-variants-section">
        <div class="variants-header">
          <h4>Message Content</h4>
        </div>

        <!-- Language Tabs -->
        <div class="language-tabs">
          <button
            type="button"
            v-for="(variant, index) in messageVariants"
            :key="index"
            class="language-tab"
            :class="{ 
              'active': activeVariantIndex === index
            }"
            @click="setActiveVariant(index)"
            :disabled="!!savedMessageId"
          >
            <i class="fa fa-language"></i>
            {{ getLanguageLabel(variant.language) }}
            <i 
              v-if="!isVariantValid(variant.language)" 
              class="fa fa-exclamation-circle validation-icon"
              title="Required fields missing"
            ></i>
            <i 
              v-else-if="variant.subject || variant.body" 
              class="fa fa-check-circle validation-icon valid"
              title="Complete"
            ></i>
          </button>
        </div>

        <!-- Active Variant Content -->
        <div class="variant-content-area" v-if="messageVariants[activeVariantIndex]">
          <div class="form-group">
            <label>Subject <span class="required">*</span></label>
            <input
              type="text"
              v-model="messageVariants[activeVariantIndex].subject"
              class="form-control"
              placeholder="Enter email subject..."
              :maxlength="maxSubjectLength"
              :readonly="!!savedMessageId"
              :disabled="!!savedMessageId"
              @input="updateVariant(activeVariantIndex)"
            />
            <div class="field-hint">
              {{ messageVariants[activeVariantIndex].subject.length }}/{{ maxSubjectLength }} characters
            </div>
          </div>

          <div class="form-group">
            <label>Message Body <span class="required">*</span></label>
            <RichTextEditor
              :value="messageVariants[activeVariantIndex].body"
              @input="updateVariantBody(activeVariantIndex, $event)"
              :disabled="!!savedMessageId"
              :show-char-count="false"
            />
            <div class="field-hint" :class="{ 'error-hint': isBodyTooLong(activeVariantIndex) }">
              {{ getBodyCharacterCount(activeVariantIndex) }}<template v-if="selectedMessageType.toLowerCase() !== 'email'">/{{ maxBodyLength }}</template> characters
            </div>
            <div v-if="isBodyTooLong(activeVariantIndex)" class="error-text">
              <i class="fa fa-exclamation-triangle"></i>
              Message body exceeds the maximum length
            </div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="messageAttachment">Attachments</label>
        <div class="attachment-field">
          <input
            id="messageAttachment"
            type="file"
            class="form-control file-input"
            multiple
            accept="image/*,.pdf,.doc,.docx,.txt"
            disabled
          />
          <div class="attachment-placeholder">
            <i class="fa fa-paperclip"></i>
            <span>Select files to attach</span>
          </div>
        </div>
        <div class="field-hint future-feature">
          <i class="fa fa-info-circle"></i>
          Note: File attachments are a future feature and not yet
          implemented
        </div>
      </div>
    </div>

    <!-- Template Selection Modal (using Teleport to render outside parent modal) -->
    <Teleport to="#modal-destination" v-if="showTemplateModal">
      <TemplateSelectionModal
        :is-visible="showTemplateModal"
        :templates="templates"
        :loading="loadingTemplates"
        @close="closeTemplateModal"
        @select-template="applyTemplate"
      />
    </Teleport>
  </div>
</template>

<script>
import RichTextEditor from "../shared/RichTextEditor.vue";
import TemplateSelectionModal from "../modals/TemplateSelectionModal.vue";

export default {
  name: "ComposeMessageStep",
  components: {
    RichTextEditor,
    TemplateSelectionModal,
  },
  props: {
    messageSubject: { type: String, required: true },
    messageContent: { type: String, required: true },
    messageType: { type: String, required: true },
    responseType: { type: String, required: true },
    selectedMessageType: { type: String, required: true },
    selectedCrisisType: { type: String, required: true },
    selectedEvent: { type: String, required: true },
    messageTypes: { type: Array, required: true },
    events: { type: Array, required: true },
    fetchingMessageTypes: { type: Boolean, required: true },
    fetchingEvents: { type: Boolean, required: true },
    savedMessageId: { type: String, default: null },
    selectedSitesCount: { type: Number, required: true },
    messageTemplates: { type: Array, required: true },
    mctServerRoute: { type: String, required: true },
    templates: { type: Array, required: true },
    loadingTemplates: { type: Boolean, required: true },
    messageContents: { type: Array, default: () => [] },
    schedulingRules: { type: Object, default: null },
    fetchingSchedulingRules: { type: Boolean, required: true },
    maxBodyLength: { type: Number, default: 1400 },
  },
  data() {
    return {
      showTemplateModal: false,
      messageVariants: [
        {
          language: 'en',
          subject: '',
          body: '',
        },
        {
          language: 'fr',
          subject: '',
          body: '',
        }
      ],
      activeVariantIndex: 0,
      maxSubjectLength: 200,
    };
  },
  watch: {
    messageSubject(newVal) {
      // Sync with English variant for backward compatibility
      const enVariant = this.messageVariants.find(v => v.language === 'en');
      if (enVariant && enVariant.subject !== newVal) {
        enVariant.subject = newVal;
        // Don't emit here to avoid infinite loop; parent will update from our emit
      }
    },
    messageContent(newVal) {
      // Sync with English variant for backward compatibility
      const enVariant = this.messageVariants.find(v => v.language === 'en');
      if (enVariant && enVariant.body !== newVal) {
        enVariant.body = newVal;
        // Don't emit here to avoid infinite loop, parent will update from our emit
      }
    },
    selectedMessageType() {
      // Re-emit message contents when message type changes
      this.emitMessageVariants();
    },
    messageVariants: {
      handler() {
        // Emit whenever any variant changes
        this.emitMessageVariants();
      },
      deep: true
    }
  },
  mounted() {
    // Initialize variants from messageContents if available
    if (this.messageContents && this.messageContents.length > 0) {
      this.initializeFromMessageContents();
    } else {
      // Fall back to legacy props if messageContents not available
      const enVariant = this.messageVariants.find(v => v.language === 'en');
      if (enVariant && (this.messageSubject || this.messageContent)) {
        enVariant.subject = this.messageSubject;
        enVariant.body = this.messageContent;
      }
    }
    
    // Emit after initialization
    this.$nextTick(() => {
      this.emitMessageVariants();
    });
  },
  computed: {
    config() {
      return this.$store.getters.config;
    },
    isEnglishValid() {
      const enVariant = this.messageVariants.find(v => v.language === 'en');
      if (!enVariant) return false;
      
      const plainBody = this.getPlainTextFromHTML(enVariant.body);
      return enVariant.subject.trim().length > 0 && plainBody.trim().length > 0;
    },
    isFrenchValid() {
      const frVariant = this.messageVariants.find(v => v.language === 'fr');
      if (!frVariant) return false;
      
      const plainBody = this.getPlainTextFromHTML(frVariant.body);
      return frVariant.subject.trim().length > 0 && plainBody.trim().length > 0;
    },
    currentSchedulingRule() {
      if (!this.schedulingRules || !this.messageType) return null;
      
      // If a specific message type is selected (not auto/empty), create a simple single-send rule
      if (this.selectedMessageType && this.selectedMessageType !== '' && this.selectedMessageType !== 'auto') {
        return {
          messageTypeCodes: [this.selectedMessageType],
          intervalMinutes: 0,
          recycleTimeMinutes: 0,
          maxCycles: 1
        };
      }
      
      // Map priority values to scheduling rule keys
      const priorityMap = {
        'urgent': 'urgentPriority',
        'medium': 'mediumPriority',
        'low': 'lowPriority'
      };
      
      const ruleKey = priorityMap[this.messageType];
      return this.schedulingRules[ruleKey] || null;
    },
    schedulingFlowItems() {
      if (!this.currentSchedulingRule) return [];
      
      const items = [];
      const types = this.currentSchedulingRule.messageTypeCodes;
      
      // Muted color mapping for message types
      const colorMap = {
        'sms': '#5a6268',
        'voice': '#5a6268',
        'email': '#5a6268'
      };
      
      // Add step and wait items for each message type
      types.forEach((type, index) => {
        // Add step item
        items.push({
          key: 'step-' + type + '-' + index,
          type: 'step',
          class: 'timeline-step',
          number: index + 1,
          icon: this.getMessageTypeIcon(type),
          label: type.toUpperCase(),
          color: colorMap[type.toLowerCase()] || '#6c757d'
        });
        
        // Add wait item after each step except the last
        if (index < types.length - 1) {
          items.push({
            key: 'wait-' + type + '-' + index,
            type: 'wait',
            class: 'timeline-wait',
            label: this.currentSchedulingRule.intervalMinutes + 'min'
          });
        }
      });
      
      // Only add recycle elements if this is an automatic flow (not a specific message type)
      const isAutoFlow = !this.selectedMessageType || this.selectedMessageType === '' || this.selectedMessageType === 'auto';
      const hasMultipleCycles = this.currentSchedulingRule.maxCycles > 1;
      
      if (isAutoFlow && hasMultipleCycles) {
        // Add recycle wait
        items.push({
          key: 'wait-recycle',
          type: 'wait',
          class: 'timeline-wait timeline-wait-recycle',
          label: this.currentSchedulingRule.recycleTimeMinutes + 'min'
        });
        
        // Add restart item
        items.push({
          key: 'restart',
          type: 'restart',
          class: 'timeline-restart'
        });
      }
      
      return items;
    },
  },
  methods: {
    getPlainTextFromHTML(html) {
      if (!html) return '';
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },
    isVariantValid(languageCode) {
      const variant = this.messageVariants.find(v => v.language === languageCode);
      if (!variant) return false;
      
      const plainBody = this.getPlainTextFromHTML(variant.body);
      const hasContent = variant.subject.trim().length > 0 && plainBody.trim().length > 0;
      const isEmailType = this.selectedMessageType.toLowerCase() === 'email';
      const isNotTooLong = (isEmailType || plainBody.length <= this.maxBodyLength) && variant.subject.length <= this.maxSubjectLength;
      
      return hasContent && isNotTooLong;
    },
    isBodyTooLong(index) {
      const variant = this.messageVariants[index];
      if (!variant) return false;
      if (this.selectedMessageType.toLowerCase() === 'email') return false;
      
      const plainBody = this.getPlainTextFromHTML(variant.body);
      return plainBody.length > this.maxBodyLength;
    },
    getBodyCharacterCount(index) {
      const variant = this.messageVariants[index];
      if (!variant) return 0;
      
      const plainBody = this.getPlainTextFromHTML(variant.body);
      return plainBody.length;
    },
    setActiveVariant(index) {
      this.activeVariantIndex = index;
    },
    updateVariant(index) {
      const variant = this.messageVariants[index];
      // Emit changes for the English variant to maintain backward compatibility
      if (variant.language === 'en') {
        this.$emit('update:messageSubject', variant.subject);
        this.$emit('update:messageContent', variant.body);
      }
      // Emit all variants in the new format
      this.emitMessageVariants();
    },
    updateVariantBody(index, value) {
      this.messageVariants[index].body = value;
      this.updateVariant(index);
    },
    emitMessageVariants() {
      // Build messageContents array in the required format
      const messageContents = [];
      
      this.messageVariants.forEach(variant => {
        const langCode = variant.language.toUpperCase();
        
        // Always add subject content (even if empty) for validation
        messageContents.push({
          content: variant.subject || '',
          languageCode: langCode,
          contentType: 'message.subject',
          messageTypeCode: this.selectedMessageType || 'auto'
        });
        
        // Always add body content (even if empty) for validation
        messageContents.push({
          content: variant.body || '',
          languageCode: langCode,
          contentType: 'message.body',
          messageTypeCode: this.selectedMessageType || 'auto'
        });
      });
      
      this.$emit('update:messageContents', messageContents);
    },
    initializeFromMessageContents() {
      // Parse messageContents array and populate messageVariants
      const languages = ['en', 'fr'];
      
      languages.forEach(lang => {
        const langCode = lang.toUpperCase();
        const variant = this.messageVariants.find(v => v.language === lang);
        
        if (!variant) return;
        
        // Find subject content for this language
        const subjectContent = this.messageContents.find(
          c => c.languageCode === langCode && c.contentType === 'message.subject'
        );
        
        // Find body content for this language
        const bodyContent = this.messageContents.find(
          c => c.languageCode === langCode && c.contentType === 'message.body'
        );
        
        // Update variant with found content
        if (subjectContent) {
          variant.subject = subjectContent.content || '';
        }
        if (bodyContent) {
          variant.body = bodyContent.content || '';
        }
      });
    },
    getLanguageLabel(code) {
      const languages = {
        'en': 'English',
        'fr': 'Français',
      };
      return languages[code] || code;
    },
    openNewEventPage() {
      // Open the new event page in a new tab
      const url = this.config.optionalFeatures.mct.powerAppUrl;
      window.open(url, '_blank');
    },
    getSelectedMessageTypeDescription() {
      if (!this.selectedMessageType) return '';
      const messageType = this.messageTypes.find(mt => mt.code === this.selectedMessageType);
      return messageType && messageType.description ? messageType.description : '';
    },
    getMessageTypeIcon(type) {
      const icons = {
        'sms': 'fa fa-comment',
        'voice': 'fa fa-phone',
        'email': 'fa fa-envelope',
        'teams': 'fa fa-users'
      };
      return icons[type.toLowerCase()] || 'fa fa-paper-plane';
    },
    formatMessageTypes(types) {
      if (!types || types.length === 0) return '';
      return types.map(t => t.toUpperCase()).join(', ');
    },
    async openTemplateModal() {
      if (!this.selectedEvent) return;

      // Get crisis type from selected event
      const selectedEventObj = this.events.find(e => e.id === this.selectedEvent);
      if (!selectedEventObj || !selectedEventObj.crisisTypeId) {
        return;
      }

      this.showTemplateModal = true;
      this.$emit('fetch-templates', selectedEventObj.crisisTypeId);
    },
    closeTemplateModal() {
      this.showTemplateModal = false;
    },
    applyTemplate(template) {
      // Populate English variant
      const enVariant = this.messageVariants.find(v => v.language === 'en');
      if (enVariant) {
        enVariant.subject = template.subjectEn || '';
        enVariant.body = template.bodyEn || '';
        const enIndex = this.messageVariants.indexOf(enVariant);
        this.updateVariant(enIndex);
      }
      
      // Populate French variant if available in template
      const frVariant = this.messageVariants.find(v => v.language === 'fr');
      if (frVariant) {
        frVariant.subject = template.subjectFr || '';
        frVariant.body = template.bodyFr || '';
        const frIndex = this.messageVariants.indexOf(frVariant);
        this.updateVariant(frIndex);
      }
      
      // Set message type if available
      if (template.messageTypeCode) {
        this.$emit('update:selectedMessageType', template.messageTypeCode);
      }
    },
  },
};
</script>

<style scoped>
.compose-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 20px;
}

.compose-header h3 {
  margin: 0;
  flex: 1;
}

.template-section {
  position: relative;
  flex-shrink: 0;
}

.templates-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.templates-btn:hover {
  border-color: #007bff;
  color: #007bff;
  background: #f8f9fa;
}

.template-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 320px;
  max-width: 400px;
}

.template-dropdown-header {
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  border-radius: 8px 8px 0 0;
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
}

.template-list {
  max-height: 300px;
  overflow-y: auto;
}

.template-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.template-item:last-child {
  border-bottom: none;
  border-radius: 0 0 8px 8px;
}

.template-item:hover {
  background: #f8f9fa;
}

.template-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
  font-size: 14px;
}

.template-preview {
  font-size: 12px;
  color: #6c757d;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
}

.selection-summary-compact {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #495057;
  font-weight: 500;
}

.summary-item i {
  color: #007bff;
  width: 16px;
}

.message-composer {
  max-width: 100%;
  overflow-x: hidden;
}

.message-saved-alert {
  margin-bottom: 20px;
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 4px solid #28a745;
  background-color: #d4edda;
  color: #155724;
}

.message-saved-alert i {
  margin-right: 8px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.event-field-container {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.event-field-container .form-control {
  flex: 1;
}

.subject-field-container {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.subject-field-container .form-control {
  flex: 1;
}

.select-template-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.select-template-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(23, 162, 184, 0.3);
}

.select-template-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.select-template-btn-full {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  width: 100%;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  border: 2px solid #17a2b8;
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(23, 162, 184, 0.2);
}

.select-template-btn-full:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(23, 162, 184, 0.35);
  background: linear-gradient(135deg, #138496 0%, #117a8b 100%);
  border-color: #138496;
}

.select-template-btn-full:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(23, 162, 184, 0.25);
}

.select-template-btn-full:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #6c757d;
  border-color: #6c757d;
  box-shadow: none;
}

.select-template-btn-full i {
  font-size: 16px;
}

.new-event-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.new-event-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

.new-event-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.required {
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.field-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #6c757d;
  text-align: right;
}

.field-hint.error-hint {
  color: #dc3545;
  font-weight: 600;
}

.error-text {
  display: block;
  margin-top: 4px;
  color: #dc3545;
  font-weight: 600;
}

.error-text i {
  margin-right: 4px;
}

/* Scheduling Info Display */
.scheduling-info {
  margin-top: 16px;
  padding: 0;
  background: white;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  overflow: hidden;
}

.scheduling-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  border-left: 3px solid #3498db;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #495057;
  font-size: 13px;
}

.header-left i {
  color: #3498db;
  font-size: 14px;
}

.header-left strong {
  font-weight: 600;
  color: #2c3e50;
}

.header-right {
  display: flex;
  gap: 8px;
}

.info-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: white;
  border: 1px solid #3498db;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  color: #3498db;
}

.info-badge i {
  color: #3498db;
  font-size: 9px;
}

/* Timeline Design */
.scheduling-timeline {
  display: flex;
  align-items: center;
  padding: 24px 20px;
  gap: 0;
  overflow-x: auto;
  background: white;
}

.timeline-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
}

.timeline-node {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.node-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  border: 2px solid #e9ecef;
  position: relative;
  z-index: 2;
}

.node-icon i {
  color: white;
  font-size: 18px;
}

.node-number {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #3498db;
  color: white;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(52, 152, 219, 0.3);
  z-index: 3;
}

.timeline-label {
  font-size: 10px;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.timeline-wait {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
}

.timeline-connector {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.connector-line {
  width: 50px;
  height: 2px;
  background: repeating-linear-gradient(
    90deg,
    #dee2e6,
    #dee2e6 4px,
    transparent 4px,
    transparent 8px
  );
  position: relative;
}

.connector-line::after {
  content: '▸';
  position: absolute;
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 12px;
}

.connector-time {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  color: #6c757d;
  white-space: nowrap;
}

.connector-time i {
  font-size: 8px;
  color: #adb5bd;
}

.timeline-wait-recycle .connector-line {
  width: 70px;
  background: repeating-linear-gradient(
    90deg,
    #3498db,
    #3498db 4px,
    transparent 4px,
    transparent 8px
  );
}

.timeline-wait-recycle .connector-time {
  background: #e8f4fd;
  border-color: #3498db;
  color: #2c3e50;
  font-weight: 700;
}

.timeline-wait-recycle .connector-time i {
  color: #3498db;
}

.timeline-restart {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 10px 14px;
  background: #3498db;
  border-radius: 6px;
  color: white;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  box-shadow: 0 2px 4px rgba(52, 152, 219, 0.25);
}

.timeline-restart i {
  font-size: 16px;
}

/* Summary Footer */
.scheduling-summary {
  display: flex;
  gap: 20px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #6c757d;
}

.summary-item i {
  color: #3498db;
  font-size: 11px;
}

.summary-item strong {
  color: #495057;
  font-weight: 600;
}

.refresh-btn {
  margin-top: 4px;
  padding: 4px 8px;
  font-size: 12px;
  color: #007bff;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.refresh-btn:hover:not(:disabled) {
  color: #0056b3;
  text-decoration: underline;
}

.refresh-btn:disabled {
  color: #6c757d;
  cursor: not-allowed;
}

.refresh-btn i {
  font-size: 11px;
}

.future-feature {
  text-align: left !important;
  color: #856404 !important;
  background: #fff3cd;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ffeaa7;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.attachment-field {
  position: relative;
  border: 2px dashed #e9ecef;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  background: #f8f9fa;
  transition: all 0.2s ease;
}

.attachment-field:hover {
  border-color: #dee2e6;
  background: #f1f3f4;
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: not-allowed;
}

.attachment-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  pointer-events: none;
}

.attachment-placeholder i {
  font-size: 24px;
  color: #dee2e6;
}

.attachment-placeholder span {
  font-size: 14px;
  font-weight: 500;
}

/* Message Variants Section */
.message-variants-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid #e9ecef;
}

.variants-header {
  margin-bottom: 20px;
}

.variants-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 18px;
  font-weight: 700;
}

/* Language Tabs */
.language-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 0;
  border-bottom: 2px solid #e9ecef;
}

.language-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: #6c757d;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  bottom: -2px;
}

.language-tab:hover:not(:disabled) {
  color: #007bff;
  background: #f8f9fa;
}

.language-tab.active {
  color: #007bff;
  border-bottom-color: #007bff;
  background: white;
}

.language-tab:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.language-tab i {
  font-size: 16px;
}

.validation-icon {
  font-size: 14px;
  margin-left: 4px;
}

.validation-icon.valid {
  color: #28a745;
}

/* Variant Content Area */
.variant-content-area {
  padding: 24px;
  background: white;
  border: 2px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 10px 10px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .compose-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .template-section {
    align-self: flex-start;
  }

  .template-dropdown {
    right: auto;
    left: 0;
    min-width: 280px;
    max-width: 100%;
  }

  .selection-summary-compact {
    flex-direction: column;
    gap: 12px;
  }

  .language-tabs {
    overflow-x: auto;
  }

  .language-tab {
    padding: 10px 16px;
    font-size: 13px;
    white-space: nowrap;
  }

  .variant-content-area {
    padding: 16px;
  }
}
</style>
